import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  ArrowForwardOutlined as ArrowIcon,
  BusinessOutlined as BusinessIcon,
  CheckCircleOutline as CheckIcon,
  CloudUploadOutlined as UploadIcon,
  DeleteOutline as DeleteIcon,
  DescriptionOutlined as DocumentIcon,
  ForumOutlined as ForumIcon,
  LockOutlined as LockIcon,
  LogoutOutlined as LogoutIcon,
  MarkEmailReadOutlined as MessageIcon,
  SendOutlined as SendIcon,
  ShieldOutlined as ShieldIcon,
  VerifiedUserOutlined as VerifiedIcon
} from '@mui/icons-material';
import partnerQuoteService from '../services/partnerQuoteService';
import {
  CLOSED_STATUSES,
  FileAttachField,
  FilePreviewDialog,
  MessageThread,
  QuoteItemsEditor,
  QuoteItemsSummary,
  STATUS_COLORS,
  STATUS_LABELS,
  calculateQuoteItemsTotal,
  hasValidQuoteItems,
  formatDate,
  useAsyncAction
} from './quoteExchangeShared';

const getSavedPartner = () => {
  try { return JSON.parse(sessionStorage.getItem('partnerPortalUser') || 'null'); } catch { return null; }
};

const EMPTY_NEW_FORM = { subject: '', description: '', currency: 'USD', quoted_amount: '' };

const PORTAL_COLORS = {
  navy: '#081b35',
  navySoft: '#0d2d54',
  primary: '#1d63b8',
  primaryDark: '#154d91',
  accent: '#5ea6ff',
  pale: '#eaf2fb',
  background: '#eef3fb',
  surface: '#ffffff',
  text: '#0f2742',
  muted: '#5f738b'
};
const PORTAL_BORDER = 'rgba(29, 99, 184, 0.16)';
const PORTAL_SHADOW = '0 18px 55px rgba(14, 48, 90, 0.10)';

function ProcessStep({ number, icon, title, description }) {
  return (
    <Stack direction="row" spacing={1.6} alignItems="flex-start">
      <Box
        sx={{
          width: 42,
          height: 42,
          flex: '0 0 auto',
          borderRadius: 2.5,
          display: 'grid',
          placeItems: 'center',
          color: PORTAL_COLORS.primary,
          bgcolor: PORTAL_COLORS.pale,
          border: `1px solid ${PORTAL_BORDER}`
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: PORTAL_COLORS.muted, fontWeight: 800, letterSpacing: '.08em' }}>
          STEP {number}
        </Typography>
        <Typography sx={{ mt: 0.1, color: PORTAL_COLORS.text, fontWeight: 850 }}>{title}</Typography>
        <Typography variant="body2" sx={{ mt: 0.35, color: PORTAL_COLORS.muted, lineHeight: 1.6 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

function LoginPanel({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { pending, error, run } = useAsyncAction();

  const submit = async (event) => {
    event.preventDefault();
    try {
      const data = await run(() => partnerQuoteService.login(email, password), {
        fallbackMessage: 'Unable to sign in.'
      });
      onAuthenticated(data.partner);
    } catch {
      // error already captured by useAsyncAction
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: PORTAL_COLORS.navy, backgroundImage: 'radial-gradient(circle at 15% 20%, rgba(94,166,255,.20), transparent 31%), radial-gradient(circle at 85% 80%, rgba(29,99,184,.16), transparent 36%)' }}>
      <Paper component="form" onSubmit={submit} elevation={0} sx={{ width: '100%', maxWidth: 470, p: { xs: 3, sm: 5 }, borderRadius: 5, bgcolor: alpha(PORTAL_COLORS.navySoft, 0.96), color: '#f4f8fb', border: '1px solid rgba(94,166,255,.28)', boxShadow: '0 30px 80px rgba(0,0,0,.38)' }}>
        <Box sx={{ width: 54, height: 54, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: PORTAL_COLORS.accent, color: PORTAL_COLORS.navy, mb: 3 }}><ShieldIcon /></Box>
        <Typography variant="overline" sx={{ color: '#9bc8ff', fontWeight: 900, letterSpacing: '.15em' }}>SECURE PARTNER ACCESS</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900, letterSpacing: '-.04em' }}>Quotation Exchange</Typography>
        <Typography variant="body2" sx={{ mt: 1, mb: 4, color: alpha('#fff', 0.66), lineHeight: 1.7 }}>
          Submit quotation files and keep every clarification in one protected conversation with SMTLINKS.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            fullWidth type="email" label="Email" value={email}
            onChange={(event) => setEmail(event.target.value)}
            required autoComplete="username"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: alpha('#fff', 0.04) } }}
          />
          <TextField
            fullWidth type="password" label="Password" value={password}
            onChange={(event) => setPassword(event.target.value)}
            required autoComplete="current-password"
          />
          <Button
            type="submit" size="large" variant="contained" disabled={pending}
            sx={{ py: 1.35, fontWeight: 900, bgcolor: PORTAL_COLORS.accent, color: PORTAL_COLORS.navy, '&:hover': { bgcolor: '#8bc0ff' } }}
          >
            {pending ? <CircularProgress size={22} /> : 'Sign in securely'}
          </Button>
        </Stack>
        <Typography variant="caption" sx={{ mt: 3, display: 'block', color: alpha('#fff', 0.48) }}>
          Access is monitored. Do not share your credentials or upload unrelated confidential information.
        </Typography>
      </Paper>
    </Box>
  );
}

export default function PartnerPortalPage() {
  const [partner, setPartner] = useState(getSavedPartner());
  const [checking, setChecking] = useState(Boolean(sessionStorage.getItem('partnerPortalToken')));
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_NEW_FORM);
  const [newFiles, setNewFiles] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const selectedIdRef = useRef(null);
  const realtimeInFlightRef = useRef(false);
  const [filePreview, setFilePreview] = useState({ open: false, loading: false, error: '', data: null });
  const [deleteOpen, setDeleteOpen] = useState(false);

  const list = useAsyncAction();
  const detail = useAsyncAction();
  const submit = useAsyncAction();
  const deleteThreadAction = useAsyncAction();
  const [fileWarning, setFileWarning] = useState('');

  const loadDetail = async (id) => {
    setSelectedId(id);
    selectedIdRef.current = id;
    setDetailLoading(true);
    try {
      const data = await detail.run(() => partnerQuoteService.getThread(id), {
        fallbackMessage: 'Unable to load this quotation.'
      });
      setThreadDetail(data);
    } catch {
      setThreadDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const verify = async () => {
      if (!sessionStorage.getItem('partnerPortalToken')) { setChecking(false); return; }
      try {
        const data = await partnerQuoteService.getMe();
        setPartner(data.partner);
      } catch {
        partnerQuoteService.logout();
        setPartner(null);
      } finally { setChecking(false); }
    };
    verify();
  }, []);

  useEffect(() => {
    if (!partner) return;
    let active = true;
    (async () => {
      try {
        const data = await list.run(() => partnerQuoteService.listThreads(), {
          fallbackMessage: 'Unable to load quotations.'
        });
        if (!active) return;
        setThreads(data.threads || []);
        if (data.threads?.length) await loadDetail(data.threads[0].id);
      } catch {
        // error already surfaced via list.error
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  useEffect(() => {
    if (!partner || !sessionStorage.getItem('partnerPortalToken')) return undefined;
    const socket = partnerQuoteService.createRealtimeSocket();
    let refreshTimer = null;
    let disposed = false;

    const syncRealtimeState = async (event = {}) => {
      if (realtimeInFlightRef.current) return;
      realtimeInFlightRef.current = true;
      try {
        const data = await partnerQuoteService.listThreads();
        if (disposed) return;
        const nextThreads = data.threads || [];
        setThreads(nextThreads);
        const currentId = selectedIdRef.current;
        const targetId = currentId || event.threadId || nextThreads[0]?.id;
        if (!targetId) {
          setThreadDetail(null);
          return;
        }
        const nextDetail = await partnerQuoteService.getThread(targetId);
        if (disposed) return;
        setSelectedId(targetId);
        selectedIdRef.current = targetId;
        setThreadDetail(nextDetail);
      } catch (error) {
        // Normal request handling will surface authentication or API failures.
      } finally {
        realtimeInFlightRef.current = false;
      }
    };

    const handleRealtimeUpdate = (event = {}) => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => syncRealtimeState(event), 100);
    };

    const pollTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') syncRealtimeState();
    }, 3500);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncRealtimeState();
    };

    socket.on('partner_quote:updated', handleRealtimeUpdate);
    socket.on('connect', handleRealtimeUpdate);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      disposed = true;
      window.clearTimeout(refreshTimer);
      window.clearInterval(pollTimer);
      socket.off('partner_quote:updated', handleRealtimeUpdate);
      socket.off('connect', handleRealtimeUpdate);
      socket.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [partner]);

  const refreshThreads = async () => {
    const data = await partnerQuoteService.listThreads();
    setThreads(data.threads || []);
  };

  const createThread = async () => {
    try {
      const data = await submit.run(() => partnerQuoteService.createThread(
        {
          ...newForm,
          quoted_amount: newForm.quoted_amount === '' ? null : Number(newForm.quoted_amount),
          items_json: JSON.stringify(newItems)
        },
        newFiles
      ), { fallbackMessage: 'Unable to submit quotation.' });
      setNewOpen(false);
      setNewForm(EMPTY_NEW_FORM);
      setNewFiles([]);
      setNewItems([]);
      await refreshThreads();
      await loadDetail(data.id);
    } catch {
      // stays open, submit.error shows the reason
    }
  };

  const sendReply = async () => {
    if (!selectedId || (!reply.trim() && !replyFiles.length)) return;
    try {
      await submit.run(() => partnerQuoteService.addMessage(selectedId, reply, replyFiles), {
        fallbackMessage: 'Unable to send reply.'
      });
      setReply('');
      setReplyFiles([]);
      await loadDetail(selectedId);
      await refreshThreads();
    } catch {
      // submit.error shows the reason
    }
  };

  const openFilePreview = async (file) => {
    if (!thread) return;
    setFilePreview({ open: true, loading: true, error: '', data: { name: file.original_name } });
    try {
      const data = await partnerQuoteService.getPartnerFilePreview(thread.id, file);
      setFilePreview({ open: true, loading: false, error: '', data });
    } catch (error) {
      setFilePreview({ open: true, loading: false, error: error?.response?.data?.message || 'Unable to load this file.', data: null });
    }
  };

  const deleteThread = async () => {
    if (!thread) return;
    try {
      await deleteThreadAction.run(() => partnerQuoteService.deletePartnerThread(thread.id), {
        fallbackMessage: 'Unable to withdraw this quotation.'
      });
      setDeleteOpen(false);
      setSelectedId(null);
      selectedIdRef.current = null;
      setThreadDetail(null);
      const data = await partnerQuoteService.listThreads();
      const nextThreads = data.threads || [];
      setThreads(nextThreads);
      if (nextThreads[0]?.id) await loadDetail(nextThreads[0].id);
    } catch {
      // deleteThreadAction.error is shown in the confirmation dialog.
    }
  };

  if (checking) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!partner) return <LoginPanel onAuthenticated={setPartner} />;

  const thread = threadDetail?.thread;
  const messages = threadDetail?.messages || [];
  const closed = CLOSED_STATUSES.includes(thread?.status);
  const displayedError = list.error || detail.error || submit.error || fileWarning;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: PORTAL_COLORS.text,
        bgcolor: PORTAL_COLORS.background,
        backgroundImage: 'radial-gradient(circle at 8% 12%, rgba(29,99,184,.09), transparent 25%), radial-gradient(circle at 92% 88%, rgba(94,166,255,.08), transparent 28%)'
      }}
    >
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: alpha(PORTAL_COLORS.navy, .98), borderBottom: '1px solid rgba(255,255,255,.1)', backdropFilter: 'blur(16px)' }}>
        <Toolbar sx={{ width: '100%', maxWidth: 1500, mx: 'auto', px: { xs: 2, md: 3 }, gap: 1.5, minHeight: { xs: 66, md: 74 } }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: PORTAL_COLORS.accent, color: PORTAL_COLORS.navy, boxShadow: '0 8px 24px rgba(94,166,255,.28)' }}><BusinessIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, letterSpacing: '-.015em' }}>SMTLINKS Partner Exchange</Typography>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, color: alpha('#fff', .6) }}>Secure quotation workspace</Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right', mr: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>{partner.company_name}</Typography>
            <Typography variant="caption" sx={{ color: alpha('#fff', .6) }}>{partner.contact_name}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: alpha('#fff', .16), my: 1.6 }} />
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={() => { partnerQuoteService.logout(); setPartner(null); }}
            sx={{ color: alpha('#fff', .8), '&:hover': { color: '#fff', bgcolor: alpha('#fff', .08) } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Sign out</Box>
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} sx={{ maxWidth: 1500, py: { xs: 2.5, md: 4.5 }, px: { xs: 2, md: 3 } }}>
        {displayedError && (
          <Alert severity="error" onClose={() => setFileWarning('')} sx={{ mb: 2 }}>{displayedError}</Alert>
        )}
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 2.5, sm: 3.5, md: 4 },
            borderRadius: { xs: 3, md: 4.5 },
            color: '#fff',
            overflow: 'hidden',
            position: 'relative',
            border: '1px solid rgba(255,255,255,.08)',
            boxShadow: '0 22px 60px rgba(14,48,90,.18)',
            background: 'linear-gradient(118deg, #081b35 0%, #103d70 62%, #1d63b8 118%)',
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 260,
              height: 260,
              borderRadius: '50%',
              right: -70,
              top: -145,
              border: '44px solid rgba(255,255,255,.045)'
            }
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={3} alignItems={{ md: 'center' }} sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ maxWidth: 760 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.1 }}>
                <LockIcon sx={{ fontSize: 16, color: '#9bc8ff' }} />
                <Typography variant="overline" sx={{ color: '#9bc8ff', fontWeight: 900, letterSpacing: '.12em', lineHeight: 1 }}>PRIVATE WORKSPACE</Typography>
              </Stack>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.65rem' }, fontWeight: 900, letterSpacing: '-.045em', lineHeight: 1.08 }}>
                Every quotation, one clear thread.
              </Typography>
              <Typography variant="body1" sx={{ mt: 1.4, color: alpha('#fff', .74), lineHeight: 1.65 }}>
                Send proposals, receive SMTLINKS quotations, and manage revisions without email attachments.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setNewOpen(true)}
              sx={{ alignSelf: { xs: 'stretch', md: 'center' }, px: 2.5, py: 1.25, bgcolor: PORTAL_COLORS.accent, color: PORTAL_COLORS.navy, fontWeight: 900, boxShadow: '0 12px 30px rgba(5,22,44,.24)', transition: 'all 180ms ease', '&:hover': { bgcolor: '#8bc0ff', transform: 'translateY(-1px)' } }}
            >
              New quotation
            </Button>
          </Stack>
        </Paper>

        {!list.pending && threads.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: 3, md: 4 },
              overflow: 'hidden',
              border: `1px solid ${PORTAL_BORDER}`,
              boxShadow: PORTAL_SHADOW,
              bgcolor: PORTAL_COLORS.surface
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.25fr) minmax(340px, .75fr)' } }}>
              <Box sx={{ p: { xs: 3, sm: 4.5, md: 6 } }}>
                <Box sx={{ width: 58, height: 58, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: PORTAL_COLORS.pale, color: PORTAL_COLORS.primary, border: `1px solid ${PORTAL_BORDER}` }}>
                  <DocumentIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant="h4" sx={{ mt: 2.5, fontWeight: 900, color: PORTAL_COLORS.text, letterSpacing: '-.035em' }}>
                  Start your first quotation
                </Typography>
                <Typography sx={{ mt: 1.2, maxWidth: 680, color: PORTAL_COLORS.muted, lineHeight: 1.75 }}>
                  Create a secure quotation record for SMTLINKS. Your documents, questions, revisions, and final decision will remain together in one traceable conversation.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowIcon />}
                  onClick={() => setNewOpen(true)}
                  sx={{ mt: 3, px: 2.5, py: 1.2, bgcolor: PORTAL_COLORS.primary, fontWeight: 900, '&:hover': { bgcolor: PORTAL_COLORS.primaryDark } }}
                >
                  Submit first quotation
                </Button>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} sx={{ mt: 4, pt: 2.5, borderTop: `1px solid ${PORTAL_BORDER}` }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <VerifiedIcon sx={{ fontSize: 18, color: PORTAL_COLORS.primary }} />
                    <Typography variant="caption" sx={{ color: PORTAL_COLORS.muted, fontWeight: 750 }}>Private access</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <LockIcon sx={{ fontSize: 17, color: PORTAL_COLORS.primary }} />
                    <Typography variant="caption" sx={{ color: PORTAL_COLORS.muted, fontWeight: 750 }}>Audited file activity</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <CheckIcon sx={{ fontSize: 18, color: PORTAL_COLORS.primary }} />
                    <Typography variant="caption" sx={{ color: PORTAL_COLORS.muted, fontWeight: 750 }}>One source of truth</Typography>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4.5, md: 5 }, bgcolor: '#f6f9fd', borderLeft: { lg: `1px solid ${PORTAL_BORDER}` }, borderTop: { xs: `1px solid ${PORTAL_BORDER}`, lg: 0 } }}>
                <Typography variant="overline" sx={{ color: PORTAL_COLORS.primary, fontWeight: 900, letterSpacing: '.12em' }}>HOW IT WORKS</Typography>
                <Typography variant="h6" sx={{ mt: 0.4, mb: 3.2, color: PORTAL_COLORS.text, fontWeight: 900 }}>A simple, traceable process</Typography>
                <Stack spacing={3}>
                  <ProcessStep number="01" icon={<UploadIcon />} title="Submit your quotation" description="Add the scope, amount, and supporting files in a protected record." />
                  <ProcessStep number="02" icon={<ForumIcon />} title="Resolve questions together" description="Receive requests and respond without losing context across email chains." />
                  <ProcessStep number="03" icon={<CheckIcon />} title="Track the decision" description="Follow every revision and status update through completion." />
                </Stack>
              </Box>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '340px minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
            <Paper elevation={0} sx={{ borderRadius: 3.5, overflow: 'hidden', border: `1px solid ${PORTAL_BORDER}`, boxShadow: PORTAL_SHADOW, bgcolor: PORTAL_COLORS.surface }}>
              <Box sx={{ p: 2.5, borderBottom: `1px solid ${PORTAL_BORDER}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: PORTAL_COLORS.text }}>Quotation inbox</Typography>
                    <Typography variant="caption" sx={{ color: PORTAL_COLORS.muted }}>{threads.length} conversations</Typography>
                  </Box>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: PORTAL_COLORS.pale, color: PORTAL_COLORS.primary }}>
                    <MessageIcon sx={{ fontSize: 19 }} />
                  </Box>
                </Stack>
              </Box>
              {list.pending ? (
                <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
              ) : (
                <List disablePadding sx={{ maxHeight: { md: 'calc(100vh - 330px)' }, minHeight: { md: 420 }, overflow: 'auto' }}>
                  {threads.map((item) => (
                    <ListItemButton
                      key={item.id}
                      selected={selectedId === item.id}
                      onClick={() => loadDetail(item.id)}
                      sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${PORTAL_BORDER}`, '&.Mui-selected': { bgcolor: PORTAL_COLORS.pale, borderLeft: `4px solid ${PORTAL_COLORS.primary}`, pl: 2 }, '&.Mui-selected:hover': { bgcolor: '#dfeafb' } }}
                    >
                      <ListItemText
                        primary={item.subject}
                        secondary={`${item.initiated_by === 'internal' ? 'From SMTLINKS' : 'Sent by you'} · ${item.reference_number} · ${STATUS_LABELS[item.status] || item.status}`}
                        primaryTypographyProps={{ fontWeight: 850, noWrap: true, color: PORTAL_COLORS.text }}
                        secondaryTypographyProps={{ noWrap: true, sx: { mt: .45, color: PORTAL_COLORS.muted } }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3.5, minWidth: 0, minHeight: 590, border: `1px solid ${PORTAL_BORDER}`, boxShadow: PORTAL_SHADOW, overflow: 'hidden', bgcolor: PORTAL_COLORS.surface }}>
              {detailLoading ? (
                <Box sx={{ minHeight: 590, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
              ) : !thread ? (
                <Box sx={{ minHeight: 590, display: 'grid', placeItems: 'center', textAlign: 'center', p: 3 }}>
                  <Box><MessageIcon sx={{ fontSize: 54, color: '#78918d' }} /><Typography variant="h6" fontWeight={900}>Select a quotation</Typography></Box>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: { xs: 2.5, md: 3.5 }, py: 2.5, bgcolor: '#f8fafd', borderBottom: `1px solid ${PORTAL_BORDER}` }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={900} color={PORTAL_COLORS.text} noWrap>{thread.subject}</Typography>
                        <Typography variant="caption" sx={{ color: PORTAL_COLORS.muted }}>{thread.initiated_by === 'internal' ? 'Quotation from SMTLINKS' : 'Quotation sent by you'} · {thread.reference_number} · Updated {formatDate(thread.updated_at)}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start' }}>
                        <Chip label={STATUS_LABELS[thread.status] || thread.status} color={STATUS_COLORS[thread.status]} sx={{ fontWeight: 850 }} />
                        {thread.initiated_by === 'partner' && !['accepted', 'closed'].includes(thread.status) && (
                          <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
                            Withdraw
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                  <QuoteItemsSummary items={threadDetail?.items || []} currency={thread.currency} />
                  <Stack spacing={1.5} sx={{ p: { xs: 2, md: 3 }, minHeight: 350, maxHeight: '52vh', overflowY: 'auto', bgcolor: '#f1f5fa' }}>
                    <MessageThread messages={messages} viewerType="partner" onOpenFile={openFilePreview} />
                  </Stack>
                  <Divider />
                  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                    <TextField
                      fullWidth multiline minRows={3}
                      label={closed ? 'Conversation closed' : 'Reply or explain your revision'}
                      value={reply} onChange={(event) => setReply(event.target.value)} disabled={closed}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 1.2 }}>
                      <FileAttachField files={replyFiles} onChange={setReplyFiles} disabled={closed} onValidationError={(errors) => setFileWarning(errors.join(' '))} />
                      <Button variant="contained" endIcon={<SendIcon />} disabled={closed || submit.pending || (!reply.trim() && !replyFiles.length)} onClick={sendReply} sx={{ fontWeight: 850, bgcolor: PORTAL_COLORS.primary, '&:hover': { bgcolor: PORTAL_COLORS.primaryDark } }}>
                        Send reply
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        )}
      </Container>

      <Dialog open={deleteOpen} onClose={() => !deleteThreadAction.pending && setDeleteOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ color: PORTAL_COLORS.text, fontWeight: 900 }}>Withdraw quotation?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: PORTAL_COLORS.muted, lineHeight: 1.7 }}>
            This removes <Box component="span" sx={{ color: PORTAL_COLORS.text, fontWeight: 900 }}>{thread?.subject}</Box> from both active inboxes. SMTLINKS retains an audit record for security and traceability.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>This action cannot be reversed from the portal.</Alert>
          {deleteThreadAction.error && <Alert severity="error" sx={{ mt: 1.5 }}>{deleteThreadAction.error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteThreadAction.pending}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={deleteThread} disabled={deleteThreadAction.pending}>
            {deleteThreadAction.pending ? <CircularProgress size={20} /> : 'Withdraw quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      <FilePreviewDialog
        open={filePreview.open}
        loading={filePreview.loading}
        error={filePreview.error}
        preview={filePreview.data}
        onClose={() => setFilePreview({ open: false, loading: false, error: '', data: null })}
        onDownload={() => partnerQuoteService.downloadPreview(filePreview.data)}
      />

      <Dialog
        open={newOpen}
        onClose={() => !submit.pending && setNewOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', border: `1px solid ${PORTAL_BORDER}`, boxShadow: '0 30px 90px rgba(8, 36, 75, .24)' } }}
      >
        <DialogTitle sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 2.5, bgcolor: '#f7f9fd' }}>
          <Stack direction="row" spacing={1.6} alignItems="center">
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: PORTAL_COLORS.pale, color: PORTAL_COLORS.primary }}>
              <UploadIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: PORTAL_COLORS.text, fontWeight: 900 }}>Submit a quotation</Typography>
              <Typography variant="body2" sx={{ mt: .2, color: PORTAL_COLORS.muted }}>Create one traceable record for this commercial proposal.</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
          <Stack spacing={2.25}>
            <TextField label="Subject" value={newForm.subject} onChange={(event) => setNewForm((prev) => ({ ...prev, subject: event.target.value }))} required />
            <TextField multiline minRows={4} label="Description / scope" value={newForm.description} onChange={(event) => setNewForm((prev) => ({ ...prev, description: event.target.value }))} />
            <QuoteItemsEditor
              items={newItems}
              currency={newForm.currency}
              onChange={(nextItems) => {
                setNewItems(nextItems);
                const total = calculateQuoteItemsTotal(nextItems);
                if (total > 0) setNewForm((prev) => ({ ...prev, quoted_amount: total.toFixed(2) }));
              }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0, 1fr)' }, gap: 2 }}>
              <TextField select fullWidth label="Currency" value={newForm.currency} onChange={(event) => setNewForm((prev) => ({ ...prev, currency: event.target.value }))}>
                {['USD', 'CNY', 'MXN', 'EUR'].map((currency) => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
              </TextField>
              <TextField
                fullWidth type="number" label="Total quoted amount" value={newForm.quoted_amount}
                onChange={(event) => setNewForm((prev) => ({ ...prev, quoted_amount: event.target.value }))}
                inputProps={{ min: 0, step: '0.01' }}
                helperText={newItems.length ? 'Calculated automatically from quoted items; you may adjust it.' : ''}
              />
            </Box>
            <Box sx={{ p: 2, borderRadius: 2.5, border: '1px dashed #9dbbe0', bgcolor: '#f5f8fd' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: .6, color: PORTAL_COLORS.muted, fontWeight: 850, letterSpacing: '.04em' }}>SUPPORTING FILES</Typography>
              <FileAttachField files={newFiles} onChange={setNewFiles} onValidationError={(errors) => setFileWarning(errors.join(' '))} />
            </Box>
            <Alert icon={<LockIcon fontSize="inherit" />} severity="info" sx={{ border: `1px solid ${PORTAL_BORDER}`, bgcolor: '#eef5fd' }}>
              Up to 5 files, 15 MB each. Files are private and access is audited.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.25, bgcolor: '#f7f9fd' }}>
          <Button onClick={() => setNewOpen(false)} disabled={submit.pending}>Cancel</Button>
          <Button
            variant="contained" onClick={createThread}
            disabled={submit.pending || !newForm.subject.trim() || (!newForm.description.trim() && !newFiles.length && !hasValidQuoteItems(newItems))}
            endIcon={!submit.pending ? <ArrowIcon /> : null}
            sx={{ bgcolor: PORTAL_COLORS.primary, fontWeight: 900, '&:hover': { bgcolor: PORTAL_COLORS.primaryDark } }}
          >
            {submit.pending ? <CircularProgress size={20} /> : 'Submit securely'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
